import { translationsApi } from "../../utils/api";
import { languageName } from "../../utils/helpers";
import { editorName, rowId } from "./TranslationRow";
import { reviewReasonLabel } from "./reviewReasons";

/**
 * A translation row's four actions (#839), shared by the Translations page and the per-document panel
 * (#840) so both say and do the same thing. `run(key, action, message)` performs a write and re-reads
 * whatever lists the row; `suggest(key, action)` runs a call that writes nothing (Retranslate), so
 * nothing is re-read — which would also remount a row rewritten meanwhile and lose the suggestion.
 * `contextEventId(row)` names the event whose context Retranslate's prompt is built from.
 */
export function translationRowActions({ run, suggest, contextEventId }) {
  const save = (row, text) =>
    run(
      `${rowId(row)}:save`,
      () => translationsApi.edit(row, text),
      `Saved. Readers in ${languageName(row.locale)} see it wherever this text appears.`
    );

  const approve = (row) =>
    run(`${rowId(row)}:approve`, () => translationsApi.approve(row), "Approved. Readers are served it as it is.");

  // A suggestion, not a write: `show` puts it in the row's box, where the admin saves or cancels it.
  // A refusal or a cut-off reply has no text to show.
  const retranslate = (row, show) =>
    suggest(`${rowId(row)}:retranslate`, async () => {
      const suggestion = await translationsApi.retranslate(row, contextEventId(row));
      if (suggestion.text == null) return `No suggestion: ${reviewReasonLabel(suggestion.review_reason, row.locale)}.`;
      show(suggestion);
      return "Suggestion ready: review it, then Save or Cancel.";
    });

  const discard = (row) => {
    const edit = row.edited_by ? ` This deletes the reviewed translation by ${editorName(row)}.` : "";
    if (
      !window.confirm(
        `Discard this ${languageName(row.locale)} translation? Readers see the source text until the next nightly run translates it again.${edit}`
      )
    )
      return;
    run(`${rowId(row)}:discard`, () => translationsApi.discard(row), "Discarded. The next nightly run translates it again.");
  };

  return { save, approve, retranslate, discard };
}
